import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import CommonStyles from "../InputFieldsController/InputFieldsController.module.css";
import {
  FieldProps,
  getFieldContainerStyle,
} from "../InputFieldsController/InputFieldsController.utils";
import LabeledInputWrapper from "../InputFieldsController/LabeledInputWrapper";

interface CurrencyInputProps {
  field: FieldProps;
  handleChange: (fieldId: string, event: any) => void;
  handleBlur: (fieldId: string) => void;
  handleSelect: (fieldId: string) => void;
}

/**
 * CurrencyInput formats and stores the value as US-style currency string (e.g. "12,345.00").
 * Preserves cursor position to avoid jumping on comma/decimal insert.
 */
const CurrencyInput: React.FC<CurrencyInputProps> = ({
  field,
  handleChange,
  handleBlur,
  handleSelect,
}) => {
  const {
    id,
    placeholder,
    value,
    example,
    disabled,
    isValid = true,
    error,
    show = true,
    selected,
    currencyFormat = "integer",
  } = field;

  const [inputValue, setInputValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;
    const input = e.target;
    const selectionStart = input.selectionStart ?? rawInput.length;
  
    // Remove all formatting (non-digits and periods)
    const digitsOnly = rawInput.replace(/[^0-9.]/g, "");
  
    const floatVal = parseFloat(digitsOnly);
    if (isNaN(floatVal)) {
      setInputValue("");
      handleChange(id, { target: { value: "" } });
      return;
    }
  
    const formatted =
      currencyFormat === "integer"
        ? new Intl.NumberFormat("en-US", {
            maximumFractionDigits: 0,
          }).format(floatVal)
        : new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(floatVal);
  
    // Count digits before caret in raw input
    const rawBeforeCaret = rawInput.slice(0, selectionStart);
    const digitCountBeforeCaret = (rawBeforeCaret.match(/\d/g) || []).length;
  
    // Find the caret position in the formatted string
    let caretPos = 0;
    let digitsSeen = 0;
    while (caretPos < formatted.length && digitsSeen < digitCountBeforeCaret) {
      if (/\d/.test(formatted[caretPos])) {
        digitsSeen++;
      }
      caretPos++;
    }
  
    setInputValue(formatted);
    handleChange(id, { target: { value: formatted } });
  
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(caretPos, caretPos);
      }
    }, 0);
  };
  
  if (!show) return null;

  const containerStyle = getFieldContainerStyle(field);

  return (
    <div style={containerStyle}>
      <LabeledInputWrapper field={field}>
        <Form.Control
          id={id}
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={handleCurrencyChange}
          onBlur={() => handleBlur(id)}
          onFocus={() => handleSelect(id)}
          isInvalid={!isValid}
          className={`${CommonStyles.customInput} ${
            selected ? CommonStyles.selectedField : ""
          } ${disabled ? CommonStyles.disabledField : ""}`}
        />
        {!isValid && error && (
          <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        )}
      </LabeledInputWrapper>

      {example && <small className="text-muted">Example: {example}</small>}
    </div>
  );
};

export default CurrencyInput;
